/** Tab index for "Contracts" on Payer Rules (TabView `activeIndex`). */
export const CONTRACTS_TAB_INDEX = 3;

/** Tab index for the combined "Base Rate" tab (files + configs subtabs). */
export const BASE_RATE_TAB_INDEX = 2;

/** Sub-tab values under Base Rate (query param `baseSubtab`). */
export const BASE_RATE_SUBTAB_FILES = "files";
export const BASE_RATE_SUBTAB_CONFIGS = "configs";
export const BASE_RATE_SUBTAB_CUSTOM_CPT_VOLUME = "custom-cpt-volume";

export const BASE_RATE_FILES_DEEP_LINK = `/payer-rules?tab=${BASE_RATE_TAB_INDEX}&baseSubtab=${BASE_RATE_SUBTAB_FILES}`;
export const BASE_RATE_CONFIGS_DEEP_LINK = `/payer-rules?tab=${BASE_RATE_TAB_INDEX}&baseSubtab=${BASE_RATE_SUBTAB_CONFIGS}`;

/** Tab index for "Market Price Datasets". */
export const MARKET_PRICES_TAB_INDEX = 4;

/** Tab index for "EDI Conversion Files". */
export const EDI_CONVERSION_TAB_INDEX = 5;

/**
 * Deep-link URL for the Custom CPT Volume sub-tab under Source Fee Schedules.
 * Used when navigating back from the Custom CPT Volume detail page.
 */
export const CUSTOM_CPT_VOLUME_DEEP_LINK = `/payer-rules?tab=${BASE_RATE_TAB_INDEX}&baseSubtab=${BASE_RATE_SUBTAB_CUSTOM_CPT_VOLUME}`;

/** Tab index for "Hierarchy Rules" (shifted down after Custom CPT Volume was nested). */
export const HIERARCHY_RULES_TAB_INDEX = "11";

// Fee Schedule Types matching backend
export const FeeScheduleType = {
  HOSPITAL_OPPS_NATIONAL: "HOSPITAL_OPPS_NATIONAL",
  HOSPITAL_INPATIENT: "HOSPITAL_INPATIENT",
  ASC: "ASC",
  PHYSICIAN_FACILITY: "PHYSICIAN_FACILITY",
  PHYSICIAN_NON_FACILITY: "PHYSICIAN_NON_FACILITY",
} as const;

export type FeeScheduleTypeValue =
  (typeof FeeScheduleType)[keyof typeof FeeScheduleType];

export const FEE_SCHEDULE_TYPE_OPTIONS = [
  {
    label: "Hospital Outpatient",
    value: FeeScheduleType.HOSPITAL_OPPS_NATIONAL,
  },
  {
    label: "Hospital Inpatient",
    value: FeeScheduleType.HOSPITAL_INPATIENT,
  },
  { label: "ASC", value: FeeScheduleType.ASC },
  { label: "Physician (Facility)", value: FeeScheduleType.PHYSICIAN_FACILITY },
  {
    label: "Physician (Non-Facility)",
    value: FeeScheduleType.PHYSICIAN_NON_FACILITY,
  },
];

export const FEE_SCHEDULE_TYPE_LABELS: Record<string, string> = {
  HOSPITAL_OPPS_NATIONAL: "Hospital Outpatient",
  HOSPITAL_INPATIENT: "Hospital Inpatient",
  ASC: "ASC",
  PHYSICIAN_FACILITY: "Physician (Facility)",
  PHYSICIAN_NON_FACILITY: "Physician (Non-Facility)",
};

export const RULE_TYPE_OPTIONS = [
  { label: "Fixed Rate", value: "FIXED_RATE" },
  { label: "Source Override", value: "SOURCE_OVERRIDE" },
  { label: "Swap", value: "SWAP" },
];

export const RULE_TYPE_LABELS: Record<string, string> = {
  FIXED_RATE: "Fixed Rate",
  SOURCE_OVERRIDE: "Source Override",
  SWAP: "Swap",
};

export const SOURCE_OVERRIDE_OPTIONS = [
  { label: "CLFS", value: "CLFS" },
  { label: "ASP", value: "ASP" },
  { label: "DMEPOS", value: "DMEPOS" },
  { label: "PPRVU", value: "PPRVU" },
  { label: "Addendum B", value: "ADDENDUM_B" },
];

// IPPS Scenario options for Hospital Inpatient base rate file generation
export const IPPS_SCENARIO_OPTIONS = [
  { label: "Quality Data + EHR User", value: "quality_ehr" },
  { label: "Quality Data + NO EHR User", value: "quality_no_ehr" },
  { label: "NO Quality Data + EHR User", value: "no_quality_ehr" },
  { label: "NO Quality Data + NO EHR User", value: "no_quality_no_ehr" },
];

export const STATUS_OPTIONS = [
  { label: "Enabled", value: true },
  { label: "Disabled", value: false },
];

// DMEPOS State options extracted from DMEPOS fee schedule CSV
export const DMEPOS_STATE_OPTIONS = [
  { label: "AL (NR) - Alabama Non-Rural", value: "AL_NR" },
  { label: "AL (R) - Alabama Rural", value: "AL_R" },
  { label: "AR (NR) - Arkansas Non-Rural", value: "AR_NR" },
  { label: "AR (R) - Arkansas Rural", value: "AR_R" },
  { label: "AZ (NR) - Arizona Non-Rural", value: "AZ_NR" },
  { label: "AZ (R) - Arizona Rural", value: "AZ_R" },
  { label: "CA (NR) - California Non-Rural", value: "CA_NR" },
  { label: "CA (R) - California Rural", value: "CA_R" },
  { label: "CO (NR) - Colorado Non-Rural", value: "CO_NR" },
  { label: "CO (R) - Colorado Rural", value: "CO_R" },
  { label: "CT (NR) - Connecticut Non-Rural", value: "CT_NR" },
  { label: "CT (R) - Connecticut Rural", value: "CT_R" },
  { label: "DC (NR) - District of Columbia Non-Rural", value: "DC_NR" },
  { label: "DC (R) - District of Columbia Rural", value: "DC_R" },
  { label: "DE (NR) - Delaware Non-Rural", value: "DE_NR" },
  { label: "DE (R) - Delaware Rural", value: "DE_R" },
  { label: "FL (NR) - Florida Non-Rural", value: "FL_NR" },
  { label: "FL (R) - Florida Rural", value: "FL_R" },
  { label: "GA (NR) - Georgia Non-Rural", value: "GA_NR" },
  { label: "GA (R) - Georgia Rural", value: "GA_R" },
  { label: "IA (NR) - Iowa Non-Rural", value: "IA_NR" },
  { label: "IA (R) - Iowa Rural", value: "IA_R" },
  { label: "ID (NR) - Idaho Non-Rural", value: "ID_NR" },
  { label: "ID (R) - Idaho Rural", value: "ID_R" },
  { label: "IL (NR) - Illinois Non-Rural", value: "IL_NR" },
  { label: "IL (R) - Illinois Rural", value: "IL_R" },
  { label: "IN (NR) - Indiana Non-Rural", value: "IN_NR" },
  { label: "IN (R) - Indiana Rural", value: "IN_R" },
  { label: "KS (NR) - Kansas Non-Rural", value: "KS_NR" },
  { label: "KS (R) - Kansas Rural", value: "KS_R" },
  { label: "KY (NR) - Kentucky Non-Rural", value: "KY_NR" },
  { label: "KY (R) - Kentucky Rural", value: "KY_R" },
  { label: "LA (NR) - Louisiana Non-Rural", value: "LA_NR" },
  { label: "LA (R) - Louisiana Rural", value: "LA_R" },
  { label: "MA (NR) - Massachusetts Non-Rural", value: "MA_NR" },
  { label: "MA (R) - Massachusetts Rural", value: "MA_R" },
  { label: "MD (NR) - Maryland Non-Rural", value: "MD_NR" },
  { label: "MD (R) - Maryland Rural", value: "MD_R" },
  { label: "ME (NR) - Maine Non-Rural", value: "ME_NR" },
  { label: "ME (R) - Maine Rural", value: "ME_R" },
  { label: "MI (NR) - Michigan Non-Rural", value: "MI_NR" },
  { label: "MI (R) - Michigan Rural", value: "MI_R" },
  { label: "MN (NR) - Minnesota Non-Rural", value: "MN_NR" },
  { label: "MN (R) - Minnesota Rural", value: "MN_R" },
  { label: "MO (NR) - Missouri Non-Rural", value: "MO_NR" },
  { label: "MO (R) - Missouri Rural", value: "MO_R" },
  { label: "MS (NR) - Mississippi Non-Rural", value: "MS_NR" },
  { label: "MS (R) - Mississippi Rural", value: "MS_R" },
  { label: "MT (NR) - Montana Non-Rural", value: "MT_NR" },
  { label: "MT (R) - Montana Rural", value: "MT_R" },
  { label: "NC (NR) - North Carolina Non-Rural", value: "NC_NR" },
  { label: "NC (R) - North Carolina Rural", value: "NC_R" },
  { label: "ND (NR) - North Dakota Non-Rural", value: "ND_NR" },
  { label: "ND (R) - North Dakota Rural", value: "ND_R" },
  { label: "NE (NR) - Nebraska Non-Rural", value: "NE_NR" },
  { label: "NE (R) - Nebraska Rural", value: "NE_R" },
  { label: "NH (NR) - New Hampshire Non-Rural", value: "NH_NR" },
  { label: "NH (R) - New Hampshire Rural", value: "NH_R" },
  { label: "NJ (NR) - New Jersey Non-Rural", value: "NJ_NR" },
  { label: "NJ (R) - New Jersey Rural", value: "NJ_R" },
  { label: "NM (NR) - New Mexico Non-Rural", value: "NM_NR" },
  { label: "NM (R) - New Mexico Rural", value: "NM_R" },
  { label: "NV (NR) - Nevada Non-Rural", value: "NV_NR" },
  { label: "NV (R) - Nevada Rural", value: "NV_R" },
  { label: "NY (NR) - New York Non-Rural", value: "NY_NR" },
  { label: "NY (R) - New York Rural", value: "NY_R" },
  { label: "OH (NR) - Ohio Non-Rural", value: "OH_NR" },
  { label: "OH (R) - Ohio Rural", value: "OH_R" },
  { label: "OK (NR) - Oklahoma Non-Rural", value: "OK_NR" },
  { label: "OK (R) - Oklahoma Rural", value: "OK_R" },
  { label: "OR (NR) - Oregon Non-Rural", value: "OR_NR" },
  { label: "OR (R) - Oregon Rural", value: "OR_R" },
  { label: "PA (NR) - Pennsylvania Non-Rural", value: "PA_NR" },
  { label: "PA (R) - Pennsylvania Rural", value: "PA_R" },
  { label: "RI (NR) - Rhode Island Non-Rural", value: "RI_NR" },
  { label: "RI (R) - Rhode Island Rural", value: "RI_R" },
  { label: "SC (NR) - South Carolina Non-Rural", value: "SC_NR" },
  { label: "SC (R) - South Carolina Rural", value: "SC_R" },
  { label: "SD (NR) - South Dakota Non-Rural", value: "SD_NR" },
  { label: "SD (R) - South Dakota Rural", value: "SD_R" },
  { label: "TN (NR) - Tennessee Non-Rural", value: "TN_NR" },
  { label: "TN (R) - Tennessee Rural", value: "TN_R" },
  { label: "TX (NR) - Texas Non-Rural", value: "TX_NR" },
  { label: "TX (R) - Texas Rural", value: "TX_R" },
  { label: "UT (NR) - Utah Non-Rural", value: "UT_NR" },
  { label: "UT (R) - Utah Rural", value: "UT_R" },
  { label: "VA (NR) - Virginia Non-Rural", value: "VA_NR" },
  { label: "VA (R) - Virginia Rural", value: "VA_R" },
  { label: "VT (NR) - Vermont Non-Rural", value: "VT_NR" },
  { label: "VT (R) - Vermont Rural", value: "VT_R" },
  { label: "WA (NR) - Washington Non-Rural", value: "WA_NR" },
  { label: "WA (R) - Washington Rural", value: "WA_R" },
  { label: "WI (NR) - Wisconsin Non-Rural", value: "WI_NR" },
  { label: "WI (R) - Wisconsin Rural", value: "WI_R" },
  { label: "WV (NR) - West Virginia Non-Rural", value: "WV_NR" },
  { label: "WV (R) - West Virginia Rural", value: "WV_R" },
  { label: "WY (NR) - Wyoming Non-Rural", value: "WY_NR" },
  { label: "WY (R) - Wyoming Rural", value: "WY_R" },
  { label: "AK (NR) - Alaska Non-Rural", value: "AK_NR" },
  { label: "AK (R) - Alaska Rural", value: "AK_R" },
  { label: "HI (NR) - Hawaii Non-Rural", value: "HI_NR" },
  { label: "HI (R) - Hawaii Rural", value: "HI_R" },
  { label: "PR (NR) - Puerto Rico Non-Rural", value: "PR_NR" },
  { label: "PR (R) - Puerto Rico Rural", value: "PR_R" },
  { label: "VI (NR) - Virgin Islands Non-Rural", value: "VI_NR" },
  { label: "VI (R) - Virgin Islands Rural", value: "VI_R" },
];

// US States for ASC Wage Index search (2-letter codes)
export const US_STATE_OPTIONS = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
  { label: "District of Columbia", value: "DC" },
  { label: "Puerto Rico", value: "PR" },
  { label: "Virgin Islands", value: "VI" },
];
